import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { usePricing, productOf, bookingHref } from "../lib/catalog";
import CtaLink, { btnPrimary } from "../components/CtaLink";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CalendarCheck, Gift } from "lucide-react";

const NEXT_48 = {
  blueprint: ["Within a few hours: we run your full AI Revenue Readiness Score™ scan.", "Within 24 hours: we build your prioritized fix list and ROI estimate.", "Within 48 hours: you receive your report, recommended plan and a recorded video walkthrough."],
  default: ["Today: your secure account and receipt are confirmed.", "Within 24 hours: we review your onboarding details and run your baseline scan.", "Within 48 hours: your implementation plan is ready and authorized work begins."],
};
const CMS = ["WordPress admin", "Shopify staff account", "Squarespace / Wix contributor", "Webflow editor", "Custom site / developer access", "Other / not sure"];

function Intake({ product, sessionId, email }) {
  const [f, setF] = useState({ cms_access_type: "", website_url: "", cms_notes: "", primary_contact_name: "", primary_contact_email: email || "", primary_contact_phone: "", booking_system: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (email) setF((x) => ({ ...x, primary_contact_email: x.primary_contact_email || email })); }, [email]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.cms_access_type || !f.primary_contact_name || !f.primary_contact_email) { toast.error("Please complete the required fields"); return; }
    setBusy(true);
    try { await api.post("/thank-you/intake", { ...f, product, session_id: sessionId }); setDone(true); toast.success("Onboarding details received"); }
    catch { toast.error("Could not submit — please check your email address."); } finally { setBusy(false); }
  };
  if (done) return <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-800" data-testid="intake-done">Thanks — your onboarding details are in. We'll be in touch within one business day.</div>;
  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-7 space-y-3" data-testid="intake-form">
      <h2 className="font-heading text-lg font-bold text-slate-900">Onboarding form</h2>
      <Input placeholder="Website URL" value={f.website_url} onChange={set("website_url")} className="h-11" data-testid="intake-website" />
      <select value={f.cms_access_type} onChange={set("cms_access_type")} className="w-full h-11 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="intake-cms">
        <option value="">Website login / CMS access type *</option>
        {CMS.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <Input placeholder="Access notes (who can invite us, no passwords please)" value={f.cms_notes} onChange={set("cms_notes")} className="h-11" data-testid="intake-cms-notes" />
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Primary contact name *" value={f.primary_contact_name} onChange={set("primary_contact_name")} className="h-11" data-testid="intake-contact-name" />
        <Input type="email" placeholder="Primary contact email *" value={f.primary_contact_email} onChange={set("primary_contact_email")} className="h-11" data-testid="intake-contact-email" />
      </div>
      <Input placeholder="Primary contact phone" value={f.primary_contact_phone} onChange={set("primary_contact_phone")} className="h-11" data-testid="intake-contact-phone" />
      <Input placeholder="Booking / ordering system used (e.g. Calendly, OpenTable, Shopify)" value={f.booking_system} onChange={set("booking_system")} className="h-11" data-testid="intake-booking-system" />
      <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold" data-testid="intake-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit onboarding details"}</Button>
    </form>
  );
}

export default function ThankYou() {
  const { product } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { data: pricing } = usePricing();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!sessionId) { setInfo({ found: false }); return undefined; }
    let tries = 0; let t;
    const poll = async () => {
      try {
        const { data } = await api.get(`/thank-you/${sessionId}`);
        setInfo(data);
        if (data.found && !data.paid && tries++ < 10) t = setTimeout(poll, 2000);
      } catch { setInfo({ found: false }); }
    };
    poll();
    return () => clearTimeout(t);
  }, [sessionId]);

  const p = productOf(pricing, product);
  const base = info?.purchased_at ? new Date(info.purchased_at) : new Date();
  const creditDate = new Date(info?.blueprint_credit_expires_at || base.getTime() + 30 * 864e5).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="thank-you-page">
      <div className="flex items-start gap-4">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 shrink-0" />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900" data-testid="thank-you-title">Thank you — {p?.name || "your order"} is confirmed.</h1>
          <p className="text-slate-500 mt-2" data-testid="thank-you-status">{!info ? "Confirming your payment…" : info.found ? (info.paid ? "Payment received." : "Payment is processing — this page updates automatically.") : "Your receipt is on its way from Stripe."}</p>
        </div>
      </div>
      {product === "blueprint" && (
        <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50 p-5 flex items-center gap-3" data-testid="blueprint-credit">
          <Gift className="h-5 w-5 text-orange-600 shrink-0" />
          <p className="text-slate-800 font-medium">Your $497 is credited toward any plan until {creditDate}.</p>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-8 mt-10">
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="next-48-hours">
            <h2 className="font-heading text-lg font-bold text-slate-900">What happens in the next 48 hours</h2>
            <ol className="mt-4 space-y-3">{(NEXT_48[product] || NEXT_48.default).map((s, i) => <li key={i} className="flex gap-3 text-sm text-slate-600"><span className="h-6 w-6 shrink-0 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>{s}</li>)}</ol>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-indigo-600" /> Book your kickoff</h2>
            <p className="text-sm text-slate-500 mt-1">Optional — pick a time that suits you.</p>
            <CtaLink to={bookingHref(pricing?.booking?.strategy_call_url)} className={`${btnPrimary} mt-4`} testid="kickoff-link">Book my kickoff call</CtaLink>
          </div>
        </div>
        <Intake product={product} sessionId={sessionId} email={info?.email} />
      </div>
    </div>
  );
}
